import { useState } from "react";
import { Loader2, Users, Download } from "lucide-react";
import { api } from "@/lib/api";

export default function Admin() {
  const [key, setKey] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/waitlist", { headers: { "x-admin-key": key } });
      setData(res.data);
    } catch (err) {
      setError("Invalid admin key.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!data || !data.entries.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Email", "Plan", "Joined"],
      ...data.entries.map((e) => [e.email, e.plan || "", new Date(e.created_at).toISOString()]),
    ];
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stylo-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main data-testid="admin-page" className="relative z-10 min-h-screen bg-stone-50 px-6 py-16">
      <div className="max-w-3xl mx-auto">
        <a href="/" className="font-serif text-3xl">Stylo<span style={{ color: "#E63946" }}>.</span></a>
        <h1 className="mt-8 text-3xl font-bold text-stone-900">Waitlist Admin</h1>

        <form onSubmit={load} className="mt-6 flex gap-3 max-w-md">
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-key-input"
            className="flex-1 h-12 px-4 rounded-lg border border-stone-200 bg-white outline-none focus:ring-2 focus:ring-stone-900"
          />
          <button
            type="submit"
            data-testid="admin-load-button"
            className="h-12 px-6 rounded-lg bg-stone-900 text-white font-semibold flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "View"}
          </button>
        </form>
        {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-error">{error}</p>}

        {data && (
          <div className="mt-10" data-testid="admin-results">
            <div className="flex flex-wrap items-center gap-4">
              <div className="inline-flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-6 py-4">
                <Users className="w-6 h-6 text-stone-700" />
                <div>
                  <div className="text-3xl font-bold text-stone-900" data-testid="admin-count">{data.count}</div>
                  <div className="text-sm text-stone-500">total signups</div>
                </div>
              </div>
              <button
                onClick={downloadCsv}
                disabled={!data.entries.length}
                data-testid="admin-export-button"
                className="h-12 px-5 rounded-lg border border-stone-300 bg-white text-stone-800 font-semibold flex items-center gap-2 hover:bg-stone-100 transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> Download CSV
              </button>
            </div>

            <div className="mt-8 rounded-xl border border-stone-200 bg-white overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-stone-100 text-stone-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3 font-medium">Plan</th>
                    <th className="px-5 py-3 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={e.id} className="border-t border-stone-100" data-testid="admin-row">
                      <td className="px-5 py-3 text-stone-900">{e.email}</td>
                      <td className="px-5 py-3 text-stone-600">{e.plan || "—"}</td>
                      <td className="px-5 py-3 text-stone-500">{new Date(e.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
